import { DEFAULT_RESOURCE_CONFIG } from '../constants/DEFAULT_RESOURCE_CONFIG';
import type { ResourceConfig } from '../interfaces/ResourceConfig';
import { resourceStore } from './resourceStore';
import { mergeResourceConfig } from './mergeResourceConfig';

/**
 * @group Functions
 * @summary <blockquote>Registers a ResourceConfig in the current context.</blockquote>
 *
 * @param resourceConfig - The ResourceConfig to register
 * @param key - Optional unique identifier (auto-generated if not provided)
 *
 * @example
 * ```typescript
 * import { registerResource } from '@shared/config';
 * 
 * registerResource({
 *   auth: { type: ResourceType.PAGE, resource: PAGE_RESOURCES.ABOUT, verbs: [] },
 *   reporting: { development: { transport: 'posthog', samplingRate: 1.0 } }
 * });
 * ```
 */
export function registerResource(resourceConfig: ResourceConfig, key?: string): void {
  // Early return on client - HOCs call this but it's a no-op on client
  // Client-side config is managed by ClientResourceProvider instead
  if (typeof window !== 'undefined') {
    return;
  }

  // Merge with DEFAULT_RESOURCE_CONFIG, with provided config taking precedence
  // Use centralized merge function to ensure consistent merging behavior
  const mergedConfig = mergeResourceConfig(
    {
      ...DEFAULT_RESOURCE_CONFIG,
      auth: resourceConfig.auth, // Ensure auth is provided (required field)
    } as ResourceConfig,
    {
      ...resourceConfig,
      auth: {
        ...resourceConfig.auth,
        requireAuth: resourceConfig.auth.requireAuth ?? false, // Default to false if not provided
      },
    }
  );

  // Register in request-scoped stack (server only)
  resourceStore.register(mergedConfig, key);
} 